import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import type { ManagerRole } from '../api/managerApi';
import { DataTable, LoadingState, inlineFieldSx, useErrorToast, type DataTableColumn } from '../components/common';
import { useEventContext } from '../contexts/EventContext';
import { loadMyManagerProfile } from '../services/managerService';
import { TimingPoint, timingPointService } from '../services/timingPointService';

const iconStroke = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const EditIcon = () => (
  <svg {...iconStroke}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg {...iconStroke}>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

const DUPLICATE_TIMING_POINT_MESSAGE = '같은 이름의 계측 지점이 이미 존재합니다.';

function normalizeTimingPointName(name: string) {
  return name.trim();
}

function hasDuplicateTimingPointName(
  timingPoints: TimingPoint[],
  timingPointName: string,
  excludedId?: number
) {
  const normalizedName = normalizeTimingPointName(timingPointName);

  return timingPoints.some((timingPoint) =>
    timingPoint.id !== excludedId &&
    normalizeTimingPointName(timingPoint.timingPointName) === normalizedName
  );
}

function TimingPointsPage() {
  const { selectedEventId } = useEventContext();
  const [timingPoints, setTimingPoints] = useState<TimingPoint[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const { showError, errorToast } = useErrorToast();
  const canManageTimingPoints = managerRole === 'SUPER' || managerRole === 'MIDDLE';

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (selectedEventId == null) return;
    setIsLoading(true);
    timingPointService
      .list(selectedEventId)
      .then(setTimingPoints)
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [selectedEventId, showError]);

  const handleConfirm = async () => {
    if (!canManageTimingPoints) return;
    const timingPointName = normalizeTimingPointName(newName);
    if (!timingPointName || selectedEventId == null) return;
    if (hasDuplicateTimingPointName(timingPoints, timingPointName)) {
      showError(new Error(DUPLICATE_TIMING_POINT_MESSAGE));
      return;
    }
    try {
      await timingPointService.create(selectedEventId, {
        eventId: selectedEventId,
        timingPointName,
      });
      const updated = await timingPointService.list(selectedEventId);
      setTimingPoints(updated);
      setNewName('');
      setIsAdding(false);
    } catch (error) {
      showError(error);
    }
  };

  const handleCancel = () => {
    setNewName('');
    setIsAdding(false);
  };

  const handleUpdateConfirm = async () => {
    if (!canManageTimingPoints) return;
    const timingPointName = normalizeTimingPointName(editingName);
    if (!timingPointName || editingId === null || selectedEventId == null) return;
    if (hasDuplicateTimingPointName(timingPoints, timingPointName, editingId)) {
      showError(new Error(DUPLICATE_TIMING_POINT_MESSAGE));
      return;
    }
    try {
      await timingPointService.update(selectedEventId, editingId, {
        timingPointName,
      });
      setTimingPoints((prev) =>
        prev.map((p) => (p.id === editingId ? { ...p, timingPointName } : p))
      );
      setEditingId(null);
      setEditingName('');
    } catch (error) {
      showError(error);
    }
  };

  const handleUpdateCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleDelete = async (row: TimingPoint) => {
    if (!canManageTimingPoints) return;
    if (selectedEventId == null) return;
    try {
      await timingPointService.delete(selectedEventId, row.id);
      setTimingPoints((prev) => prev.filter((p) => p.id !== row.id));
    } catch (error) {
      showError(error);
    }
  };

  const columns: DataTableColumn<TimingPoint>[] = [
    {
      key: 'timingPointName',
      header: '계측 지점',
      align: 'center',
      render: (row) => {
        if (row.id !== editingId) return row.timingPointName;
        return (
          <InputBase
            autoFocus
            fullWidth
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleUpdateConfirm();
              if (e.key === 'Escape') handleUpdateCancel();
            }}
            sx={{ ...inlineFieldSx, fontSize: 12, height: 30, '& input': { textAlign: 'center' } }}
          />
        );
      },
    },
    ...(canManageTimingPoints
      ? [
          {
            key: '_actions',
            header: '',
            width: 88,
            align: 'right' as const,
            render: (row: TimingPoint) => {
              if (row.id === editingId) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" color="primary" onClick={handleUpdateConfirm}>✓</IconButton>
                    <IconButton size="small" onClick={handleUpdateCancel}>✕</IconButton>
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', color: '#94a3b8' }}>
                  <IconButton
                    size="small"
                    aria-label="계측 지점 수정"
                    sx={{ color: 'inherit', '&:hover': { color: '#2563eb' } }}
                    onClick={() => { setEditingId(row.id); setEditingName(row.timingPointName); }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="계측 지점 삭제"
                    sx={{ color: 'inherit', '&:hover': { color: '#dc2626' } }}
                    onClick={() => handleDelete(row)}
                  >
                    <TrashIcon />
                  </IconButton>
                </Box>
              );
            },
          },
        ]
      : []),
  ];

  const addRow = canManageTimingPoints && isAdding ? (
    <TableRow>
      <TableCell align="center">
        <InputBase
          autoFocus
          fullWidth
          placeholder="포인트명 입력"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleConfirm();
            if (e.key === 'Escape') handleCancel();
          }}
          sx={{ ...inlineFieldSx, fontSize: 12, height: 30, '& input': { textAlign: 'center' } }}
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton size="small" color="primary" onClick={handleConfirm}>✓</IconButton>
          <IconButton size="small" onClick={handleCancel}>✕</IconButton>
        </Box>
      </TableCell>
    </TableRow>
  ) : null;

  if (selectedEventId == null) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <p className="page-section">Event</p>
          <h1>계측 지점 관리</h1>
          <Box sx={{ mt: 3, color: '#8a94a6', fontSize: 14 }}>
            선택된 대회가 없습니다. 대회 목록에서 대회를 먼저 선택해 주세요.
          </Box>
        </div>
      </section>
    );
  }

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <p className="page-section">Event</p>
        <h1>계측 지점 관리</h1>
        <p className="page-description">
          계측 지점(Timing Point)은 참가자의 기록을 측정하는 출발, 중간, 도착 지점입니다. 현재 메뉴에서는 대회 기록 측정에 사용할 계측지점(Timing Point)를 관리합니다.
        </p>
        <div className="page-table">
          {isLoading ? (
            <LoadingState message="계측 지점을 불러오는 중입니다." />
          ) : (
            <DataTable
              columns={columns}
              rows={timingPoints}
              rowKey="id"
              footer={addRow}
            />
          )}
          {canManageTimingPoints && !isAdding && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1.5, display: 'block', mx: 'auto' }}
              onClick={() => setIsAdding(true)}
            >
              + 추가하기
            </Button>
          )}
        </div>
      </div>
      {errorToast}
    </section>
  );
}

export default TimingPointsPage;
